import { Alert, Stack } from '@mui/material';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { MAX_TRANSFER_CALLS } from '../../../constants/agentConstants';
import { useIsFeatureEnabled } from '../../../hooks/useIsFeatureEnabled';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import { Agent, AgentCapability } from '../../../interfaces/Agent';
import { WorkflowType } from '../../../interfaces/App';
import { FeatureGate } from '../../../interfaces/FeatureGate';
import { updateAgent } from '../../../services/agentService';
import Modal from '../../shared/Modal/Modal';
import WorkflowStepLabel from '../../shared/WorkflowStepLabel/WorkflowStepLabel';
import BookAppointmentsCapabilitySection from './BookAppointmentsCapability';
import ChooseCapabilityV1 from './ChooseCapabilityV1';
import EmployeeDirectoryUpload from './EmployeeDirectoryUpload';
import TransferCallsCapabilityV1 from './TranferCallsCapabilityV1';
import TransferCallsCapability from './TransferCallsCapability';

interface AgentCapabilitiesStepProps {
  label?: string;
  stepLabel?: string;
  description?: string;
  agentId?: string;
  usedAgentPhoneNumbers: (string | undefined)[];
  workflow?: WorkflowType;
  agent?: Agent;
}

const AgentCapabilitiesStep: React.FC<AgentCapabilitiesStepProps> = ({
  label,
  stepLabel,
  description,
  agentId,
  usedAgentPhoneNumbers,
  workflow,
  agent,
}) => {
  const { t } = useTranslation();
  const displayMessage = useSnackBar();
  const isBookingCapabilityEnabled = useIsFeatureEnabled(
    FeatureGate.BOOKING_CAPABILITY
  );

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingToggleDirection, setPendingToggleDirection] = useState<
    'on' | 'off' | null
  >(null);

  const { watch, setValue } = useFormContext();
  const transferCalls = watch('transfer_calls') || [];
  const bookAppointments = watch('insert_appointment') || [];

  const isEmployeeDirectory = transferCalls === 'employee_directory';

  const handleToggleEmployeeDirectory = () => {
    setPendingToggleDirection(isEmployeeDirectory ? 'off' : 'on');
    setConfirmModalOpen(true);
  };

  const handleConfirmToggle = async () => {
    // During CREATE there is no real agent yet (agentId is just a client-side
    // placeholder) — only update local form state. The chosen mode is sent to
    // the backend as part of the create payload once the agent actually exists.
    const canPersistToBackend = workflow === WorkflowType.EDIT && agentId;
    // The backend replaces external_capabilities wholesale, so resend the
    // agent's saved appointments or this save deletes them. Saved (not form)
    // values, so unsaved appointment edits aren't persisted early.
    const savedAppointments = agent?.external_capabilities?.insert_appointment;
    const preservedCapabilities = savedAppointments?.length
      ? { insert_appointment: savedAppointments }
      : {};

    if (pendingToggleDirection === 'off') {
      setValue('transfer_calls', [
        { department_name: '', phone_number: '+352' },
      ]);
      if (canPersistToBackend) {
        try {
          await updateAgent(agentId, {
            external_capabilities: {
              transfer_calls: null,
              ...preservedCapabilities,
            },
          });
        } catch {
          displayMessage(
            'Failed to update transfer calls capability.',
            'error'
          );
        }
      }
    } else {
      setValue('transfer_calls', 'employee_directory');
      if (canPersistToBackend) {
        try {
          await updateAgent(agentId, {
            external_capabilities: {
              transfer_calls: 'employee_directory',
              ...preservedCapabilities,
            },
          });
        } catch {
          displayMessage('Failed to enable Employee Directory mode.', 'error');
        }
      }
    }
    setPendingToggleDirection(null);
  };

  const shouldShowTransferCalls =
    isEmployeeDirectory ||
    (Array.isArray(transferCalls) && transferCalls.length > 0);
  const shouldShowBookAppointments = bookAppointments.length > 0;

  const isCreateWorkflow = workflow === WorkflowType.CREATE;

  // During CREATE nothing has been saved to the backend yet — toggling only
  // clears unsaved entries in this form, which is not a destructive/permanent
  // action the way it is once an agent already exists (EDIT).
  const confirmMessage = isCreateWorkflow
    ? pendingToggleDirection === 'on'
      ? t(
          'If you proceed, the transfer call entries you have added so far in this form will be cleared.'
        )
      : t(
          'If you proceed, your selected employee directory file will be cleared.'
        )
    : pendingToggleDirection === 'on'
      ? t(
          'If you proceed, all of the items manually entered here (if there is any) related to this agent will be deleted accordingly and cannot be reverted. So, please do this with caution.'
        )
      : t(
          'If you proceed, all of the items of your employee directory related to this agent will be deleted accordingly and cannot be reverted. So, please do this with caution.'
        );

  const confirmDescription = isCreateWorkflow
    ? t(
        'This only clears unsaved entries in this form. You can re-enter them at any time.'
      )
    : undefined;

  return (
    <div>
      <WorkflowStepLabel
        stepLabel={stepLabel}
        label={label ?? t('Agent Capabilities')}
        description={description}
      />

      <Modal
        showButton={false}
        modalShouldBeOpen={confirmModalOpen}
        mainPromptText={confirmMessage}
        description={confirmDescription}
        confirmText={t('Proceed')}
        denyText={t('Cancel')}
        confirmButtonColor="error"
        callback={handleConfirmToggle}
        setChangeStatusModalOpen={setConfirmModalOpen}
      />

      {isBookingCapabilityEnabled ? (
        <Stack direction="column" gap="1rem">
          <ChooseCapabilityV1
            capabilities={[
              {
                label: t('Transfer calls'),
                value: AgentCapability.TRANSFER_CALLS,
              },
              {
                label: t('Book appointments'),
                value: AgentCapability.INSERT_APPOINTMENT,
              },
            ]}
            agent={agent}
            onToggleEmployeeDirectory={handleToggleEmployeeDirectory}
          />
          {isEmployeeDirectory && agentId && (
            <EmployeeDirectoryUpload
              agentId={agentId}
              onRemove={() => setValue('transfer_calls', [])}
              onToggleEmployeeDirectory={handleToggleEmployeeDirectory}
              deferUpload={workflow === WorkflowType.CREATE}
            />
          )}
          {!isEmployeeDirectory && shouldShowTransferCalls && (
            <TransferCallsCapabilityV1
              maxTransferCalls={MAX_TRANSFER_CALLS}
              usedAgentPhoneNumbers={usedAgentPhoneNumbers}
              onToggleEmployeeDirectory={handleToggleEmployeeDirectory}
            />
          )}
          {shouldShowBookAppointments && (
            <BookAppointmentsCapabilitySection
              agentId={agentId}
              workflow={workflow}
            />
          )}
        </Stack>
      ) : isEmployeeDirectory ? (
        agentId ? (
          <EmployeeDirectoryUpload
            agentId={agentId}
            onRemove={() => setValue('transfer_calls', [])}
          />
        ) : (
          <Alert severity="info">
            {t(
              'Transfer calls for this agent are managed via the employee directory. No manual configuration is required.'
            )}
          </Alert>
        )
      ) : (
        <TransferCallsCapability
          maxTransferCalls={MAX_TRANSFER_CALLS}
          usedAgentPhoneNumbers={usedAgentPhoneNumbers}
        />
      )}
    </div>
  );
};

export default AgentCapabilitiesStep;
